import React from 'react'
import { useTranslation } from 'react-i18next'
import { PagingSidePanelType, Box as Section, SidePanel, Tabs } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { Transaction } from 'types/transaction'
import { TransactionType } from 'types/transaction/TransactionType'
import { highlightRowBasedOnId } from 'utils/table/table'
import { getSupportedPaymentTypes } from 'utils/utils'
import { useTransactionDetails } from '../hooks/useTransactionDetails'
import InternalTransferTransactionActivity from './PaymentTemplates/InternalTransferTransactionActivity'
import { SidePanelBody } from './SidePanelBody'
import { SidePanelHeaderWallet } from './SidePanelHeaderWallet'

interface SidePanelSummaryProps {
  isOpen: boolean
  paging: PagingSidePanelType
  transactionDetails: Transaction
  onClose: () => void
}
export const SidePanelSummary = ({
  isOpen,
  paging,
  transactionDetails,
  onClose,
}: SidePanelSummaryProps) => {
  const { t } = useTranslation()

  const {
    amountWithCurrency,
    iconUrls,
    transactionType,
    transactionSubType,
    transactionReference,
    currencyFrom,
    currencyTo,
    smallTabsId,
    status,
    hasProofOfPayment,
    externalReference,
    bvnkNetworkBeneficiary,
    bvnkNetworkSender,
  } = useTransactionDetails(transactionDetails)

  const isInternalTransfer = transactionType === TransactionType.INTERNAL_TRANSFER
  const isBvnkNetworkTransfer = transactionType === TransactionType.BVNK_NETWORK_TRANSFER
  const bannerSpacing = useComponentHeight(appBannersId)

  const supportedPaymentTypes = getSupportedPaymentTypes()

  const transactionRowId = `${transactionDetails?.id}-${transactionDetails?.hash}-${transactionDetails?.dateCreated}`

  return (
    <SidePanel
      id="side-panel-summary-transaction"
      isOpen={isOpen}
      paging={paging}
      onClose={onClose}
      activeRowId={transactionRowId}
      onActiveRowChange={highlightRowBasedOnId}
      topOffset={bannerSpacing}
      header={
        <SidePanelHeaderWallet
          data={{
            iconUrls,
            amountWithCurrency,
            transactionReference,
            transactionType,
            transactionSubType,
            currencyFrom,
            currencyTo,
            status,
            hasProofOfPayment,
            externalReference,
            bvnkNetworkBeneficiary,
            bvnkNetworkSender,
          }}
          supportedPaymentTypes={supportedPaymentTypes}
        />
      }
      externalStyle={{ top: `${bannerSpacing}px` }}
      body={
        <Section tag="section" width="full">
          {!(
            (isBvnkNetworkTransfer || isInternalTransfer) &&
            !!transactionDetails?.context?.transactions?.length
          ) ? (
            <SidePanelBody
              transactionDetails={transactionDetails}
              supportedPaymentTypes={supportedPaymentTypes}
            />
          ) : (
            <Tabs
              tabs={[
                {
                  id: 'overview',
                  header: t('overview'),
                  content: (
                    <SidePanelBody
                      transactionDetails={transactionDetails}
                      supportedPaymentTypes={supportedPaymentTypes}
                    />
                  ),
                  testId: 'sidepanel-overview-tab',
                },
                ...((isBvnkNetworkTransfer || isInternalTransfer) &&
                !!transactionDetails?.context?.transactions?.length
                  ? [
                      {
                        id: 'activity',
                        header: t('transactions.sideBar.activity'),
                        content: (
                          <InternalTransferTransactionActivity
                            transactionActivities={transactionDetails?.context?.transactions}
                          />
                        ),
                        testId: 'sidepanel-activity-tab',
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </Section>
      }
    />
  )
}
